import React, { useState, useEffect, useMemo } from 'react';
import * as Bixby from '$/core';
import { AgGridReact } from 'ag-grid-react';
import { ColumnApi, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import NoDataFound from '$/components/global/noData';
import AnalyticsHeaderFilter from '$/components/analyticsHeaderFilter';
import { colorCode, leverageColorCode } from '$/pages/client/clo-analytics/heatMap';

interface IIndustryAnalyticsProps {
  dataForGrid?: Bixby.Models.CompanyAnalytics[];
  marketAnalytics: Bixby.Models.CompanyAnalytics[] | undefined;
  heatMapActive: boolean;
  IsIndustryMapping: boolean;
  gridView: string;
}

const IndustryAnalytics: React.FC<IIndustryAnalyticsProps> = props => {
  const { dataForGrid, marketAnalytics, heatMapActive, IsIndustryMapping, gridView } = props;
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [portfolioColumnsActive, setPortfolioColumnsActive] = useState(true as boolean);
  const [transactionCompColumnsActive, setTransactionCompColumnsActive] = useState(true as boolean);

  const analyticsMetrics: Bixby.Enums.AnalyticsMetrics[] = Object.values(Bixby.Enums.AnalyticsMetrics);

  const topQuartileData = useMemo(() => {
    if (!marketAnalytics) return marketAnalytics;
    return IsIndustryMapping
      ? marketAnalytics.find(x => x.name[2] === 'Top Quartile')
      : marketAnalytics.find(x => x.name[1] === 'Top Quartile');
  }, [IsIndustryMapping, marketAnalytics]);

  const medianData = useMemo(() => {
    if (!marketAnalytics) return marketAnalytics;
    return IsIndustryMapping
      ? marketAnalytics.find(x => x.name[2] === 'Median')
      : marketAnalytics.find(x => x.name[1] === 'Median');
  }, [IsIndustryMapping, marketAnalytics]);

  const bottomQuartileData = useMemo(() => {
    if (!marketAnalytics) return marketAnalytics;
    return IsIndustryMapping
      ? marketAnalytics.find(x => x.name[2] === 'Bottom Quartile')
      : marketAnalytics.find(x => x.name[1] === 'Bottom Quartile');
  }, [IsIndustryMapping, marketAnalytics]);

  useEffect(() => {
    if (gridView == Bixby.Constants.ANALYTICS_VIEW_ALL || gridView == Bixby.Constants.ANALYTICS_PORTFOLIO) {
      setPortfolioColumnsActive(true);
    } else {
      setPortfolioColumnsActive(false);
    }

    if (gridView == Bixby.Constants.ANALYTICS_VIEW_ALL || gridView == Bixby.Constants.ANALYTICS_TRANSACTION_COMPS) {
      setTransactionCompColumnsActive(true);
    } else {
      setTransactionCompColumnsActive(false);
    }
  }, [gridView]);

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const valueFormatter = (params: any, formatter: (value: any) => string, fallback: string) => {
    if (params.value !== null && params.value !== undefined) {
      return formatter(params.value);
    } else if (!params.node.allChildrenCount) {
      return fallback;
    }
    return '';
  };

  class AnalyticsIdentifierCellRenderer {
    eGui: any;

    init(params: ICellRendererParams) {
      const newDiv = document.createElement('div');
      let value = params.value;
      const isArray = Bixby.Utils.isArray(params.value);
      if (isArray) {
        value = params.value[params.value.length - 1];
        newDiv.innerHTML = '<span>' + value + '</span>';
      } else {
        newDiv.innerHTML = '<span>' + value + '</span>';
      }

      this.eGui = newDiv.firstChild;
    }

    getGui() {
      return this.eGui;
    }
  }

  const formatCurrency = (value: number, fallback: string) => {
    const parsedValue: number = parseFloat(value as any);
    if (isNaN(parsedValue)) {
      return fallback;
    }

    // add in appropriate formatting
    let prefix = '$';
    let suffix = '';
    if (parsedValue < 0) {
      prefix = prefix + '(';
      suffix = suffix + ')';
    }

    // remove hyphen
    const fixedParsedValue = parsedValue.toFixed(2).replace('-', '');

    const currency: string = fixedParsedValue.replace(/\d(?=(\d{3})+\.)/g, '$&,').split('.')[0];

    return prefix + currency + suffix;
  };

  const formatMultiplier = (value: number) => {
    const percentage = value.toFixed(1);
    return `${percentage}x`;
  };

  const formatPercentage = (value: number, fractionDigits?: number | undefined) => {
    const percentage = (value * 100).toFixed(fractionDigits || 0);
    return `${percentage}%`;
  };

  const getCellClass = (
    params: any,
    heatMapActive: boolean,
    useLeverageColorCode: boolean,
    topQuartileDataValue?: number | null,
    medianQuartileDataValue?: number | null,
    bottomQuartileDataValue?: number | null
  ) => {
    if (
      topQuartileDataValue &&
      medianQuartileDataValue &&
      bottomQuartileDataValue &&
      params.node.key !== Bixby.Enums.AnalyticsMetrics.BottomDecile &&
      params.node.key !== Bixby.Enums.AnalyticsMetrics.TopDecile
    ) {
      if (useLeverageColorCode) {
        if (IsIndustryMapping && params.rowIndex == 0) {
          return leverageColorCode(
            true,
            params,
            topQuartileDataValue,
            medianQuartileDataValue,
            bottomQuartileDataValue
          );
        }
        return leverageColorCode(
          heatMapActive,
          params,
          topQuartileDataValue,
          medianQuartileDataValue,
          bottomQuartileDataValue
        );
      } else {
        if (IsIndustryMapping && params.rowIndex == 0) {
          return leverageColorCode(
            true,
            params,
            topQuartileDataValue,
            medianQuartileDataValue,
            bottomQuartileDataValue
          );
        }
        return colorCode(heatMapActive, params, topQuartileDataValue, medianQuartileDataValue, bottomQuartileDataValue);
      }
    } else return '';
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'expandAll', 'contractAll', 'separator', 'export'];
    return result;
  };

  const getRowHeight = (params: any) => {
    if (analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics)) {
      return 20;
    }
    return 32;
  };

  if (dataForGrid && !Bixby.Utils.isNullOrEmpty(dataForGrid)) {
    return (
      <React.Fragment>
        <section className="analytics-section">
          {dataForGrid && !Bixby.Utils.isNullOrEmpty(dataForGrid) ? (
            <div className="meterics_summary_grid analytics-data-grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    defaultColDef={{
                      flex: 1,
                      minWidth: 150,
                      resizable: true,
                      lockPinned: true,
                      filter: 'agNumberColumnFilter',
                      cellClass: 'align-center',
                    }}
                    onGridReady={onGridReady}
                    rowData={dataForGrid}
                    suppressDragLeaveHidesColumns={true}
                    suppressClipboardPaste={true}
                    suppressMovableColumns={true}
                    getContextMenuItems={getContextMenuItems}
                    treeData={true}
                    animateRows={true}
                    groupDefaultExpanded={-1}
                    groupDisplayType="custom"
                    getDataPath={function (data) {
                      return data.name;
                    }}
                    rowClassRules={{
                      metric: params => {
                        return analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics);
                      },
                    }}
                    getRowHeight={getRowHeight}
                    headerHeight={32}
                    components={{
                      headerFilter: AnalyticsHeaderFilter,
                      AnalyticsIdentifierCellRenderer: AnalyticsIdentifierCellRenderer,
                    }}
                    columnDefs={[
                      {
                        headerGroupComponent: 'headerFilter',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'name',
                            cellRenderer: 'agGroupCellRenderer',
                            valueFormatter: params => params.node?.key || '',
                            cellClassRules: {
                              sector: params => {
                                return (
                                  params.node.level === 0 &&
                                  !analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics)
                                );
                              },
                              'industry-group': params => {
                                return (
                                  params.node.level === 1 &&
                                  !analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics)
                                );
                              },
                              industry: params => {
                                return (
                                  params.node.level === 2 &&
                                  !analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics)
                                );
                              },
                            },
                            headerComponentParams: {
                              template:
                                '<div class="analytics-name"><span class="sector">Sector</span> | <span class="industry-group">Industry Group</span></div>',
                            },
                            minWidth: 350,
                            cellRendererParams: { suppressCount: true, innerRenderer: AnalyticsIdentifierCellRenderer },
                            headerClass: 'locked-header-note',
                            pinned: 'left',
                            showRowGroup: true,
                          },
                        ],
                      },
                      {
                        headerName: 'EBITDA Leverage',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'firstLienDebtGrossEbitda',
                            headerName: '1st Lien',
                            minWidth: 100,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                true,
                                topQuartileData?.firstLienDebtGrossEbitda,
                                medianData?.firstLienDebtGrossEbitda,
                                bottomQuartileData?.firstLienDebtGrossEbitda
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                          {
                            field: 'totalDebtGrossEbitda',
                            headerName: 'Total',
                            minWidth: 100,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                true,
                                topQuartileData?.totalDebtGrossEbitda,
                                medianData?.totalDebtGrossEbitda,
                                bottomQuartileData?.totalDebtGrossEbitda
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                        ],
                        suppressColumnsToolPanel: !portfolioColumnsActive,
                      },
                      {
                        headerName: 'Adj. EBITDA Leverage',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'firstLienDebtGrossAdjustedEbitda',
                            headerName: '1st Lien',
                            minWidth: 100,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                true,
                                topQuartileData?.firstLienDebtGrossAdjustedEbitda,
                                medianData?.firstLienDebtGrossAdjustedEbitda,
                                bottomQuartileData?.firstLienDebtGrossAdjustedEbitda
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                          {
                            field: 'totalDebtGrossAdjustedEbitda',
                            headerName: 'Total',
                            minWidth: 100,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                true,
                                topQuartileData?.totalDebtGrossAdjustedEbitda,
                                medianData?.totalDebtGrossAdjustedEbitda,
                                bottomQuartileData?.totalDebtGrossAdjustedEbitda
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                        ],
                        suppressColumnsToolPanel: !portfolioColumnsActive,
                      },
                      {
                        headerName: 'Interest Coverage',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'interestCoverageEbitda',
                            headerName: 'EBITDA',
                            minWidth: 130,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.interestCoverageEbitda,
                                medianData?.interestCoverageEbitda,
                                bottomQuartileData?.interestCoverageEbitda
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                          {
                            field: 'interestCoverageAdjustedEbitda',
                            headerName: 'Adj. EBITDA',
                            minWidth: 130,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.interestCoverageAdjustedEbitda,
                                medianData?.interestCoverageAdjustedEbitda,
                                bottomQuartileData?.interestCoverageAdjustedEbitda
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                        ],
                        suppressColumnsToolPanel: !portfolioColumnsActive,
                      },
                      {
                        headerName: 'Interest Coverage (After Capex)',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'interestCoverageAfterCapitalExpendituresEbitda',
                            headerName: 'EBITDA',
                            hide: !portfolioColumnsActive,
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.interestCoverageAfterCapitalExpendituresEbitda,
                                medianData?.interestCoverageAfterCapitalExpendituresEbitda,
                                bottomQuartileData?.interestCoverageAfterCapitalExpendituresEbitda
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                          {
                            field: 'interestCoverageAfterCapitalExpendituresAdjustedEbitda',
                            headerName: 'Adj. EBITDA',
                            hide: !portfolioColumnsActive,
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.interestCoverageAfterCapitalExpendituresAdjustedEbitda,
                                medianData?.interestCoverageAfterCapitalExpendituresAdjustedEbitda,
                                bottomQuartileData?.interestCoverageAfterCapitalExpendituresAdjustedEbitda
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                        ],
                        suppressColumnsToolPanel: !portfolioColumnsActive,
                      },
                      {
                        field: 'liquidity',
                        headerName: 'Liquidity (1)',
                        minWidth: 130,
                        valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                        cellClass: (params: any) =>
                          getCellClass(
                            params,
                            heatMapActive,
                            false,
                            topQuartileData?.liquidity,
                            medianData?.liquidity,
                            bottomQuartileData?.liquidity
                          ),
                        hide: !portfolioColumnsActive,
                        suppressColumnsToolPanel: !portfolioColumnsActive,
                      },
                      {
                        headerName: 'MRQ Growth %',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'revenueGrowth',
                            headerName: 'Revenue',
                            minWidth: 130,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.revenueGrowth,
                                medianData?.revenueGrowth,
                                bottomQuartileData?.revenueGrowth
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                          {
                            field: 'ebitdaGrowth',
                            headerName: 'EBITDA',
                            minWidth: 130,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.ebitdaGrowth,
                                medianData?.ebitdaGrowth,
                                bottomQuartileData?.ebitdaGrowth
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                          {
                            field: 'adjustedEbitdaGrowth',
                            headerName: 'Adj. EBITDA',
                            minWidth: 130,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.adjustedEbitdaGrowth,
                                medianData?.adjustedEbitdaGrowth,
                                bottomQuartileData?.adjustedEbitdaGrowth
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                        ],
                        suppressColumnsToolPanel: !portfolioColumnsActive,
                      },
                      {
                        headerName: 'Margin %',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'ebitdaMargin',
                            headerName: 'EBITDA',
                            minWidth: 130,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.ebitdaMargin,
                                medianData?.ebitdaMargin,
                                bottomQuartileData?.ebitdaMargin
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                          {
                            field: 'adjustedEbitdaMargin',
                            headerName: 'Adj. EBITDA',
                            minWidth: 130,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.adjustedEbitdaMargin,
                                medianData?.adjustedEbitdaMargin,
                                bottomQuartileData?.adjustedEbitdaMargin
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                          {
                            field: 'ocfMargin',
                            headerName: 'OCF',
                            minWidth: 130,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.ocfMargin,
                                medianData?.ocfMargin,
                                bottomQuartileData?.ocfMargin
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                          {
                            field: 'capitalExpendituresMargin',
                            headerName: 'OCF-Capex',
                            minWidth: 130,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.capitalExpendituresMargin,
                                medianData?.capitalExpendituresMargin,
                                bottomQuartileData?.capitalExpendituresMargin
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                        ],
                        suppressColumnsToolPanel: !portfolioColumnsActive,
                      },
                      {
                        headerName: '1st Lien Loan Details',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'firstLien1Amount',
                            headerName: '$ Amt.',
                            minWidth: 120,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.firstLien1Amount,
                                medianData?.firstLien1Amount,
                                bottomQuartileData?.firstLien1Amount
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                          {
                            field: 'firstLien1Spread',
                            headerName: 'Spread',
                            minWidth: 120,
                            hide: !portfolioColumnsActive,
                            valueFormatter: params =>
                              valueFormatter(
                                params,
                                (value: any) => {
                                  return value;
                                },
                                'NM'
                              ),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.firstLien1Spread,
                                medianData?.firstLien1Spread,
                                bottomQuartileData?.firstLien1Spread
                              ),
                            suppressColumnsToolPanel: !portfolioColumnsActive,
                          },
                        ],
                        suppressColumnsToolPanel: !portfolioColumnsActive,
                      },
                      {
                        field: 'totalEnterpriseValue',
                        headerName: 'TEV',
                        minWidth: 120,
                        valueFormatter: params =>
                          valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                        hide: !transactionCompColumnsActive,
                        cellClass: (params: any) =>
                          getCellClass(
                            params,
                            heatMapActive,
                            false,
                            topQuartileData?.totalEnterpriseValue,
                            medianData?.totalEnterpriseValue,
                            bottomQuartileData?.totalEnterpriseValue
                          ),
                        suppressColumnsToolPanel: !transactionCompColumnsActive,
                      },
                      {
                        headerName: 'Current Loan-to-Value (2)',
                        children: [
                          {
                            field: 'firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda',
                            headerName: '1st Lien',
                            minWidth: 120,
                            hide: !transactionCompColumnsActive,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda,
                                medianData?.firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda,
                                bottomQuartileData?.firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda
                              ),
                            suppressColumnsToolPanel: !transactionCompColumnsActive,
                          },
                          {
                            field: 'totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda',
                            headerName: 'Total',
                            minWidth: 120,
                            hide: !transactionCompColumnsActive,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda,
                                medianData?.totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda,
                                bottomQuartileData?.totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda
                              ),
                            suppressColumnsToolPanel: !transactionCompColumnsActive,
                          },
                        ],
                        suppressColumnsToolPanel: !transactionCompColumnsActive,
                      },
                      {
                        headerName: 'TEV /',
                        children: [
                          {
                            field: 'totalEnterpriseValueByRevenue',
                            headerName: 'Revenue',
                            minWidth: 130,
                            hide: !transactionCompColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.totalEnterpriseValueByRevenue,
                                medianData?.totalEnterpriseValueByRevenue,
                                bottomQuartileData?.totalEnterpriseValueByRevenue
                              ),
                            suppressColumnsToolPanel: !transactionCompColumnsActive,
                          },
                          {
                            field: 'totalEnterpriseValueByAdjustedManagementEbitda',
                            headerName: 'Adj. EBITDA',
                            minWidth: 130,
                            hide: !transactionCompColumnsActive,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.totalEnterpriseValueByAdjustedManagementEbitda,
                                medianData?.totalEnterpriseValueByAdjustedManagementEbitda,
                                bottomQuartileData?.totalEnterpriseValueByAdjustedManagementEbitda
                              ),
                            suppressColumnsToolPanel: !transactionCompColumnsActive,
                          },
                        ],
                        suppressColumnsToolPanel: !transactionCompColumnsActive,
                      },
                      {
                        headerName: 'Financials at transaction',
                        children: [
                          {
                            field: 'revenueAtBuyout',
                            headerName: 'Revenue',
                            minWidth: 130,
                            hide: !transactionCompColumnsActive,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.revenueAtBuyout,
                                medianData?.revenueAtBuyout,
                                bottomQuartileData?.revenueAtBuyout
                              ),
                            suppressColumnsToolPanel: !transactionCompColumnsActive,
                          },
                          {
                            field: 'managementAdjustedEbitdaAtBuyout',
                            headerName: 'Adj. EBITDA',
                            minWidth: 130,
                            hide: !transactionCompColumnsActive,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.managementAdjustedEbitdaAtBuyout,
                                medianData?.managementAdjustedEbitdaAtBuyout,
                                bottomQuartileData?.managementAdjustedEbitdaAtBuyout
                              ),
                            suppressColumnsToolPanel: !transactionCompColumnsActive,
                          },
                        ],
                        suppressColumnsToolPanel: !transactionCompColumnsActive,
                      },
                      {
                        headerName: 'Capital Structure at transaction',
                        children: [
                          {
                            field: 'netDebtPercentageOfCap',
                            headerName: 'Net Debt % of Cap',
                            hide: !transactionCompColumnsActive,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                            minWidth: 200,
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.netDebtPercentageOfCap,
                                medianData?.netDebtPercentageOfCap,
                                bottomQuartileData?.netDebtPercentageOfCap
                              ),
                            suppressColumnsToolPanel: !transactionCompColumnsActive,
                          },
                          {
                            field: 'totalEquityPercentageOfCap',
                            headerName: 'Total Equity % of Cap',
                            hide: !transactionCompColumnsActive,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                            minWidth: 200,
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.totalEquityPercentageOfCap,
                                medianData?.totalEquityPercentageOfCap,
                                bottomQuartileData?.totalEquityPercentageOfCap
                              ),
                            suppressColumnsToolPanel: !transactionCompColumnsActive,
                          },
                        ],
                        suppressColumnsToolPanel: !transactionCompColumnsActive,
                      },
                      {
                        headerName: 'Change since transaction',
                        children: [
                          {
                            field: 'cumulativeDividendsByImpliedEquityAtRisk',
                            headerName: `Dividends / Equity "at risk" (3)`,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                            minWidth: 250,
                            hide: !transactionCompColumnsActive,
                            cellClass: (params: any) =>
                              getCellClass(
                                params,
                                heatMapActive,
                                false,
                                topQuartileData?.cumulativeDividendsByImpliedEquityAtRisk,
                                medianData?.cumulativeDividendsByImpliedEquityAtRisk,
                                bottomQuartileData?.cumulativeDividendsByImpliedEquityAtRisk
                              ),
                            suppressColumnsToolPanel: !transactionCompColumnsActive,
                          },
                        ],
                        suppressColumnsToolPanel: !transactionCompColumnsActive,
                      },
                    ]}></AgGridReact>
                </div>
              </div>
            </div>
          ) : (
            <div className="analytics-not-found-section">
              <NoDataFound dataType="analytics" />
            </div>
          )}
        </section>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <section>
          <div className="portfolio_no_data">
            <NoDataFound dataType="analytics" />
          </div>
        </section>
      </React.Fragment>
    );
  }
};

export default IndustryAnalytics;
